import { useState } from "react";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import M360MarkdownLink from "@/lib/M360MarkdownLink";
import type { McpWriteProposal } from "./writeProposals";
import { writeProposalDetailRows, writeProposalReviewLabel } from "./writeProposals";

type McpWriteConfirmationCardProps = {
  proposal: McpWriteProposal;
  disabled?: boolean;
  onConfirm: (proposal: McpWriteProposal) => Promise<void>;
  onDismiss: (proposal: McpWriteProposal) => void;
};

export default function McpWriteConfirmationCard({
  proposal,
  disabled = false,
  onConfirm,
  onDismiss
}: McpWriteConfirmationCardProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(proposal);
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : "Write failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container p-4"
      data-testid="mcp-write-confirmation-card"
      aria-label="Confirm data write"
      aria-live="assertive"
      role="status"
    >
      <div>
        <p className="text-title-small">Confirm write</p>
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Review this {writeProposalReviewLabel(proposal.tool)} before saving to Backoffice.
        </p>
      </div>

      <dl className="space-y-2 rounded-lg bg-sys-brand-surface p-3" aria-label="Proposed write details">
        {writeProposalDetailRows(proposal).map((row) => (
          <div key={row.label} className="space-y-0.5">
            <dt className="text-label-medium text-sys-brand-on-surface-variant">{row.label}</dt>
            <dd className="text-body-medium whitespace-pre-wrap text-sys-brand-on-surface">
              {row.href ? <M360MarkdownLink href={row.href}>{row.value}</M360MarkdownLink> : row.value}
            </dd>
          </div>
        ))}
      </dl>

      {error ? (
        <div role="alert">
          <Banner variant="error" description={error} testId="mcp-write-confirmation-error" />
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="text" disabled={isSubmitting || disabled} onClick={() => onDismiss(proposal)}>
          Dismiss
        </Button>
        <Button
          variant="filled"
          disabled={isSubmitting || disabled}
          onClick={() => void handleConfirm()}
          testId="mcp-write-confirm"
        >
          {isSubmitting ? "Saving…" : "Confirm write"}
        </Button>
      </div>
    </section>
  );
}
